import React from "react";
import { Link } from "react-router-dom";

import styled, { css } from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button";
import Text from "components/atoms/text/Text";

import ReferenceFilterFolderView from "components/organisms/reference/ReferenceFilterFolderView";
import ReferenceFilterTagView from "components/organisms/reference/ReferenceFilterTagView";

import Constants from "utils/constants";
import { isFreePlanUser, isReadonlyUser } from "utils/user";

export const SIDE_WIDTH_MIN = 48;
export const SIDE_WIDTH_BASE = 280;
export const SIDE_WIDTH_MAX = 600;
export const SIDE_WIDTH_AUTOFOLD = 200;

const SIDE_WIDTH_MIN_PX = `${SIDE_WIDTH_MIN}px`;
const SIDE_WIDTH_BASE_PX = `${SIDE_WIDTH_BASE}px`;
const SIDE_WIDTH_MAX_PX = `${SIDE_WIDTH_MAX}px`;
const SIDEBAR_TAB = ["Folders", "Tags"];
const PRIMARY_SPACE_MENU_ITEMS = [
    { key: "search", iconName: "sideBarSearch", label: "학술검색", path: "search" },
    { key: "report", iconName: "sideBarReport", label: "동향분석", path: "report" },
    { key: "research", iconName: "sideBarResearch", label: "연구노트", path: "research" },
    { key: "manuscript", iconName: "sideBarManuscript", label: "논문작성", path: "manuscript" },
];

const SpaceMenuContainer = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${Constants.SPACE_BAR_WIDTH}px;
    width: ${SIDE_WIDTH_BASE_PX};
    min-width: ${SIDE_WIDTH_MIN_PX};
    max-width: ${SIDE_WIDTH_MAX_PX};
    height: calc(100vh - 56px);
    background-color: var(--color-White);
    resize: none;
    transition: ${({ $isResizing }) => ($isResizing ? "none" : "all 350ms ease-in-out")};
    z-index: 6;

    ${(props) =>
        props.$device.isPC &&
        css`
            width: ${SIDE_WIDTH_BASE_PX};
            min-width: ${SIDE_WIDTH_MIN_PX};
            max-width: ${SIDE_WIDTH_MAX_PX};
            position: relative;
            transition: ${({ $isResizing }) => ($isResizing ? "none" : "all 350ms ease-in-out")};
        `};
`;

const SpaceMenuContents = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: all 180ms ease-in-out;
`;

const SpaceMenuContentsWrap = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: ${({ $isFold }) => ($isFold ? "8px 2px 10px 2px" : "8px 10px 10px 10px")};
`;

const SpaceMenuTopSlot = styled.div`
    height: 56px;
    margin: 0 6px;
    flex-shrink: 0;
`;

const SpaceMenuTitleSection = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
`;

const SpaceMenuTitle = styled(Text)`
    min-width: 0;
    font-size: 1.8rem;
    font-weight: 500;
    color: var(--color-Black);
    background-color: var(--color-TitleBar);
    padding: 0px 4px;
    border-radius: 4px;
    overflow: hidden;
    white-space: nowrap;
`;

const SpaceMenuTitleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
`;

const SpaceMenuHeaderActions = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 8px;
`;

const SpaceMenuHeaderActionButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    padding: 0;
    background: transparent;
    border: 0;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 140ms ease-in;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }
`;

const SpaceMenuTitleDivider = styled.div`
    height: 1px;
    margin: 0 6px;
    background-color: var(--color-Grey3);
`;

const SpaceMenuGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 14px;
`;

const SpaceMenuNavList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
`;

const SpaceMenuSection = styled.div`
    display: flex;
    align-items: center;
    margin: 0;
    width: 100%;
    min-width: ${({ $isFold }) => ($isFold ? "0" : "200px")};
    height: 44px;
    justify-content: ${({ $isFold }) => ($isFold ? "center" : "flex-start")};
`;

const SpaceMenuItem = styled(Link)`
    position: relative;
    display: flex;
    align-items: center;
    margin: 0;
    padding: ${({ $isFold }) => ($isFold ? "0" : "0 10px 0 16px")};
    width: ${({ $isFold }) => ($isFold ? "40px" : "calc(100% - 12px)")};
    height: 40px;
    background-color: ${(props) => (props.$isActive ? "var(--color-ButtonHover4)" : "transparent")};
    border: none;
    border-radius: 4px;
    outline: none;
    cursor: pointer;
    transition: background-color 140ms ease-in;
    justify-content: ${({ $isFold }) => ($isFold ? "center" : "space-between")};

    &::before {
        content: "";
        position: absolute;
        top: 50%;
        left: 8px;
        width: 3px;
        height: 16px;
        border-radius: 2px;
        background-color: ${(props) => (props.$isActive ? "var(--color-Key)" : "transparent")};
        transform: translateY(-50%);
        transition: background-color 140ms ease-in;
        opacity: ${({ $isFold }) => ($isFold ? "0" : "1")};
    }

    &:hover {
        background-color: ${(props) => (props.$isActive ? "var(--color-ButtonHover4)" : "rgba(0, 154, 126, 0.09)")};
    }
`;

const SpaceMenuItemLeftSide = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ $isFold }) => ($isFold ? "0" : "12px")};
    justify-content: ${({ $isFold }) => ($isFold ? "center" : "flex-start")};
    width: ${({ $isFold }) => ($isFold ? "100%" : "auto")};
    min-width: 0;
    overflow: hidden;
`;

const SpaceMenuTitleText = styled(Text)`
    min-width: 0;
    font-size: 1.6rem;
    font-weight: 500;
    color: var(--color-Black);
    overflow: hidden;
    white-space: nowrap;
    ${({ $isFold }) =>
        $isFold &&
        css`
            display: none;
        `};
`;

const SpaceMenuReferenceTabsPanel = styled.div`
    width: 100%;
    border-radius: 4px;
    background-color: transparent;
    overflow: hidden;
`;

const SpaceMenuReferenceContent = styled.div`
    max-height: ${({ $isFold }) => ($isFold ? "0" : "640px")};
    opacity: ${({ $isFold }) => ($isFold ? "0" : "1")};
    overflow: hidden;
    transition:
        max-height 220ms ease-in-out,
        opacity 140ms ease-in-out;
`;

const SpaceMenuTabContainer = styled.div`
    display: flex;
    align-items: flex-start;
    margin: 6px 6px 8px 16px;
    width: calc(100% - 28px);
    min-height: 120px;
    flex-direction: column;
    flex: 1;
`;

const SpaceMenuTabButtonWrap = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    margin: 0;
    padding: 6px 8px 0 8px;
    gap: 4px;
    border-bottom: 1px solid var(--color-Grey3);
`;

const SpaceMenuTabButton = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 32px;
    padding: 0 10px;
    border: 1px solid transparent;
    border-bottom: 0;
    border-radius: 4px 4px 0 0;
    background-color: ${(props) => (props.$isActive ? "var(--color-Base1)" : "transparent")};
    cursor: pointer;

    ${(props) =>
        props.$isActive &&
        css`
            border-color: var(--color-Grey3);
        `};
`;

const SpaceMenuTabButtonText = styled(Text)`
    font-size: 1.4rem;
    font-weight: ${({ $isActive }) => ($isActive ? "600" : "500")};
    color: ${({ $isActive }) => ($isActive ? "var(--color-Key)" : "var(--color-SubBlack)")};
`;

const SpaceMenuTabButtonBar = styled.span`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: block;
    height: 1px;
    border-radius: 2px;
    background-color: ${(props) => (props.$isActive ? "var(--color-White)" : "transparent")};
`;

const SpaceMenuTabMoreButtonWrap = styled.div`
    display: flex;
    margin-left: auto;
    gap: 4px;
`;

const SpaceMenuScrollContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    width: 100%;
    padding: 10px 8px 8px 8px;
    background-color: transparent;
`;

const SpaceMenuCustomContent = styled.div`
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: ${({ $isFold }) => ($isFold ? "0" : "8px 0 0 0")};
    opacity: ${({ $isFold }) => ($isFold ? "0" : "1")};
    pointer-events: ${({ $isFold }) => ($isFold ? "none" : "auto")};
`;

const FoldToggleSection = styled(SpaceMenuSection)`
    height: 56px;
`;

const ReferenceSection = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    padding-top: 10px;
    border-top: 1px solid var(--color-Grey3);
`;

const ReferenceMenuRow = styled(SpaceMenuSection)``;

const SpaceMenuResizerWrap = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: calc(100% - 6px);
    z-index: 10;
    display: flex;
    justify-content: center;
    width: 12px;
    resize: horizontal;
    user-select: none;

    ${(props) =>
        props.$device.isPC &&
        css`
            cursor: ${({ $isFold }) => ($isFold ? "default" : "ew-resize")};

            &:hover {
                cursor: ${({ $isFold }) => ($isFold ? "default !important" : "ew-resize !important")};
                .toggleButton {
                    background-color: var(--color-Key);
                }
            }
        `};
`;

const SpaceMenuResizerLine = styled.div`
    position: relative;
    width: 3px;
    height: 100%;
    pointer-events: none;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        left: -1px;
        width: 3px;
        background-color: var(--color-Grey3);
        opacity: 0.5;
        transition:
            left 0.22s cubic-bezier(0.2, 0, 0, 1),
            opacity 0.22s cubic-bezier(0.2, 0, 0, 1),
            width 0.22s cubic-bezier(0.2, 0, 0, 1);
    }

    &::after {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        right: 1px;
        width: 3px;
        background-color: ${({ $device, $isHovering, $isFold }) =>
            $device.isPC && !$isFold && $isHovering ? "var(--color-Key)" : "transparent"};
        opacity: ${({ $device, $isHovering, $isFold }) => ($device.isPC && !$isFold && $isHovering ? "1" : "0")};
        transition: all 200ms ease;
    }
`;

const VolumeSection = styled.div`
    margin: 0 6px 6px 6px;
    padding: 16px;
    border-radius: 4px;
    border: 1px solid rgba(17, 24, 39, 0.08);
    background: var(--color-White);
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
`;

const VolumeSectionWrap = styled.div`
    margin-top: auto;
    max-height: ${({ $isFold }) => ($isFold ? "0" : "240px")};
    opacity: ${({ $isFold }) => ($isFold ? "0" : "1")};
    transform: ${({ $isFold }) => ($isFold ? "translateY(8px)" : "none")};
    pointer-events: ${({ $isFold }) => ($isFold ? "none" : "auto")};
    overflow: ${({ $isFold }) => ($isFold ? "hidden" : "visible")};
    transition:
        max-height 220ms ease-in-out,
        opacity 160ms ease-in-out,
        transform 220ms ease-in-out;
`;

const VolumeHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;
`;

const PlanTextWrap = styled.div`
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;

    > div {
        max-width: 100%;
    }
`;

const PlanText = styled(Text)`
    display: inline-block;
    max-width: 100%;
    min-width: 0;
    font-size: 1.4rem;
    font-weight: 500;
    color: var(--color-Black);
    margin-bottom: 8px;
    cursor: pointer;
    overflow: hidden;
    white-space: nowrap;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const VolumeText = styled(Text)`
    min-width: 0;
    font-size: 1.2rem;
    font-weight: 400;
    color: ${(props) => (props.$warn ? "var(--color-Warn)" : "var(--color-Black)")};
    overflow: hidden;
    white-space: nowrap;
`;

const BarWrap = styled.div`
    position: relative;
    width: 100%;
    height: 8px;
    border-radius: 4px;
    background-color: var(--color-Grey3);
    overflow: hidden;
`;

const Guage = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    height: 8px;
    background-color: ${(props) => (props.$warn ? "var(--color-Warn)" : "var(--color-Key)")};
    width: ${(props) => props.$guage || 0}%;
    transition: width 0.3s ease;
`;

const CapacityInfo = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
    font-size: 1.2rem;
    color: var(--color-Black);
    margin-top: 4px;
`;

const SpaceMenuTitleLabel = styled(Text)`
    min-width: 0;
    font-size: 1.1rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    margin-left: 4px;
    margin-bottom: 2px;
    overflow: hidden;
    white-space: nowrap;
`;

function ReferenceDetailsPanel({
    isFold,
    activeTab,
    handleTabClick,
    location,
    setToast,
    tagDelete,
    navigate,
    space_uuid,
    searchParams,
    showTagAddModal,
    setShowTagAddModal,
    showFolderAddModal,
    setShowFolderAddModal,
    user,
}) {
    const buildMergedReferenceState = (nextState = {}) => {
        // 태그와 폴더 필터가 동시에 남지 않도록 맞춘다.
        const exclusiveState = {
            ...nextState,
            ...(nextState.tags !== undefined && { folder: [] }),
            ...(nextState.folder !== undefined && { tags: [] }),
        };
        const mergedState = {
            ...(searchParams?.tags && {
                tags: searchParams.tags.split(",").map((tag) => Number(tag)),
            }),
            ...(searchParams?.search && {
                search: searchParams.search,
            }),
            ...(searchParams?.favorites && {
                favorites: true,
            }),
            ...(searchParams?.folder && {
                folder: [searchParams.folder],
            }),
            ...exclusiveState,
        };

        if (!mergedState.tags || mergedState.tags.length === 0) {
            delete mergedState.tags;
        }

        if (!mergedState.search) {
            delete mergedState.search;
        }

        if (!mergedState.favorites) {
            delete mergedState.favorites;
        }

        if (!mergedState.folder || mergedState.folder.length === 0) {
            delete mergedState.folder;
        }

        return mergedState;
    };

    return (
        <SpaceMenuReferenceContent $isFold={isFold}>
            <SpaceMenuTabContainer>
                <SpaceMenuReferenceTabsPanel>
                    <SpaceMenuTabButtonWrap>
                        {SIDEBAR_TAB.map((tab) => {
                            const isActive = activeTab === tab;

                            return (
                                <SpaceMenuTabButton key={tab} $isActive={isActive} onClick={() => handleTabClick(tab)}>
                                    <SpaceMenuTabButtonText $isActive={isActive}>{tab}</SpaceMenuTabButtonText>
                                    <SpaceMenuTabButtonBar $isActive={isActive} />
                                </SpaceMenuTabButton>
                            );
                        })}
                        <SpaceMenuTabMoreButtonWrap>
                            {activeTab === "Tags" && location.state?.tags?.length > 0 && (
                                <Tooltip message={"태그 삭제"}>
                                    <Button
                                        onlyIcon
                                        bgColor={"transparent"}
                                        hoverBgColor={"var(--color-ButtonHover4)"}
                                        onClick={() => {
                                            if (!location.state?.tags || location.state?.tags.length < 1) {
                                                setToast("삭제할 태그를 선택해주세요.", "error");
                                                return;
                                            }

                                            Confirm(
                                                "warn",
                                                "태그 삭제",
                                                "태그를 삭제하시겠습니까? 태그만 삭제되며, 파일은 유지됩니다.",
                                                "삭제",
                                                () => {
                                                    tagDelete.mutate(
                                                        {
                                                            ids: Array.from(location.state?.tags || []),
                                                        },
                                                        {
                                                            onSuccess: () => {
                                                                setToast("태그가 삭제되었습니다.", "info");
                                                                navigate(`/${space_uuid}/reference`, {
                                                                    state: buildMergedReferenceState({ tags: [] }),
                                                                });
                                                            },
                                                            onError: () => {
                                                                setToast(
                                                                    "태그 삭제에 실패했습니다. 잠시후 시도해주세요.",
                                                                    "error",
                                                                );
                                                            },
                                                        },
                                                    );
                                                },
                                            );
                                        }}
                                    >
                                        <Icon name={"delete"} size={"12"} color={"var(--color-DisabledText)"} />
                                    </Button>
                                </Tooltip>
                            )}
                            <Tooltip message={activeTab === "Tags" ? "태그 추가" : "폴더 추가"}>
                                <Button
                                    onlyIcon
                                    bgColor={"transparent"}
                                    hoverBgColor={"var(--color-ButtonHover4)"}
                                    onClick={() => {
                                        if (activeTab === "Tags") {
                                            setShowTagAddModal(true);
                                            return;
                                        }

                                        setShowFolderAddModal({
                                            type: "create",
                                            parentIndex: searchParams?.folder ? searchParams?.folder : null,
                                        });
                                    }}
                                    disabled={isReadonlyUser(user)}
                                >
                                    <Icon name={"plus"} size={"12"} color={"var(--color-DisabledText)"} />
                                </Button>
                            </Tooltip>
                        </SpaceMenuTabMoreButtonWrap>
                    </SpaceMenuTabButtonWrap>
                    <SpaceMenuScrollContainer>
                        {activeTab === "Tags" ? (
                            <ReferenceFilterTagView
                                treeId={Constants.TAG_TREE_ID_SIDEMENU}
                                onSelectItems={(items) => {
                                    navigate(`/${space_uuid}/reference`, {
                                        state: buildMergedReferenceState({ tags: items || [] }),
                                    });
                                }}
                                searchParams={searchParams}
                                showTagAddModal={showTagAddModal}
                                setShowTagAddModal={setShowTagAddModal}
                            />
                        ) : (
                            <ReferenceFilterFolderView
                                treeId={"folder-view"}
                                searchParams={searchParams}
                                onSelectItems={(items) => {
                                    navigate(`/${space_uuid}/reference`, {
                                        state: buildMergedReferenceState({ folder: items || [] }),
                                    });
                                }}
                                location={location}
                                showFolderAddModal={showFolderAddModal}
                                setShowFolderAddModal={setShowFolderAddModal}
                            />
                        )}
                    </SpaceMenuScrollContainer>
                </SpaceMenuReferenceTabsPanel>
            </SpaceMenuTabContainer>
        </SpaceMenuReferenceContent>
    );
}

function SpaceMenuBar({
    device,
    wBarRef,
    isFold,
    wBarSize,
    wBarResize,
    wBarHover,
    startWBarResizing,
    setWBarHover,
    toggleSidebar,
    spaces,
    space_uuid,
    navigate,
    isPathReference,
    activeTab,
    handleTabClick,
    location,
    setToast,
    tagDelete,
    searchParams,
    showTagAddModal,
    setShowTagAddModal,
    showFolderAddModal,
    setShowFolderAddModal,
    user,
    setShowPaidPlanSubscribeModal,
    volume,
    isVolumeWarn,
    formatBytes,
    primaryMenuActiveMap,
    customMode = false,
    customTitleLabel = "",
    customTitle = "",
    customTitleAction = null,
    customContent = null,
    showCustomVolumeSection = false,
}) {
    const currentSpaceName = spaces?.find((space) => space.uuid === space_uuid)?.name || "";

    const renderSpaceMenuItem = ({ to, isActive, iconName, label, state }) => {
        const menuItem = (
            <SpaceMenuItem $isActive={isActive} $isFold={isFold} to={to} state={state}>
                <SpaceMenuItemLeftSide $isFold={isFold}>
                    <Icon name={iconName} size={"16px"} color={isActive ? "var(--color-Key)" : undefined} />
                    <SpaceMenuTitleText $isActive={isActive} $isFold={isFold}>
                        {label}
                    </SpaceMenuTitleText>
                </SpaceMenuItemLeftSide>
            </SpaceMenuItem>
        );

        if (isFold) {
            return <Tooltip message={label}>{menuItem}</Tooltip>;
        }

        return menuItem;
    };

    const renderFoldToggleButton = () => {
        const toggleButton = (
            <SpaceMenuItem as="button" type="button" $isActive={false} $isFold={true} onClick={toggleSidebar}>
                <SpaceMenuItemLeftSide $isFold={true}>
                    <Icon name={"sidemenuOpen"} size={"16px"} color={"var(--color-Button2)"} />
                </SpaceMenuItemLeftSide>
            </SpaceMenuItem>
        );

        return <Tooltip message={"열기"}>{toggleButton}</Tooltip>;
    };

    const renderVolumeSection = () => (
        <VolumeSectionWrap $isFold={isFold}>
            <VolumeSection>
                <VolumeHeader>
                    <PlanTextWrap>
                        <Tooltip message={"요금제 확인"}>
                            <PlanText onClick={() => navigate(`/${space_uuid}/management`)}>
                                {user?.current_space?.plan?.name} 요금제
                            </PlanText>
                        </Tooltip>
                    </PlanTextWrap>
                    {isFreePlanUser(user) && (
                        <Button
                            onlyText
                            width={"72px"}
                            height={"24px"}
                            bgColor={"var(--color-Button1)"}
                            hoverBgColor={"var(--color-ButtonHover1)"}
                            fontColor={"var(--color-White)"}
                            buttonText={"업그레이드"}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setShowPaidPlanSubscribeModal(true);
                            }}
                            singleButton
                        />
                    )}
                </VolumeHeader>
                <BarWrap>
                    <Guage $guage={(volume?.used / volume?.max) * 100} $warn={isVolumeWarn} />
                </BarWrap>
                <CapacityInfo>
                    <VolumeText $warn={isVolumeWarn}>
                        {formatBytes(volume?.used)} / {formatBytes(volume?.max)}
                    </VolumeText>
                    <VolumeText $warn={isVolumeWarn}>
                        {((volume?.used / volume?.max) * 100).toFixed(0)}% 사용중
                    </VolumeText>
                </CapacityInfo>
            </VolumeSection>
        </VolumeSectionWrap>
    );

    return (
        <SpaceMenuContainer
            $device={device}
            ref={wBarRef}
            $isFold={isFold}
            $isResizing={wBarResize}
            style={{ width: `${wBarSize}px` }}
        >
            <SpaceMenuContents>
                <SpaceMenuContentsWrap $isFold={isFold}>
                    {customMode ? (
                        <>
                            <SpaceMenuTopSlot>
                                {isFold ? (
                                    <FoldToggleSection $isFold={isFold}>{renderFoldToggleButton()}</FoldToggleSection>
                                ) : (
                                    <SpaceMenuTitleSection>
                                        {customTitleLabel && (
                                            <SpaceMenuTitleLabel>{customTitleLabel}</SpaceMenuTitleLabel>
                                        )}
                                        <SpaceMenuTitleRow>
                                            <SpaceMenuTitle>{customTitle}</SpaceMenuTitle>
                                            <SpaceMenuHeaderActions>
                                                {customTitleAction}
                                                <SpaceMenuHeaderActionButton type="button" onClick={toggleSidebar}>
                                                    <Tooltip message={"닫기"}>
                                                        <Icon
                                                            name={"sidemenuClose"}
                                                            size={"16px"}
                                                            color={"var(--color-Grey1)"}
                                                        />
                                                    </Tooltip>
                                                </SpaceMenuHeaderActionButton>
                                            </SpaceMenuHeaderActions>
                                        </SpaceMenuTitleRow>
                                    </SpaceMenuTitleSection>
                                )}
                            </SpaceMenuTopSlot>
                            <SpaceMenuTitleDivider />
                            <SpaceMenuCustomContent $isFold={isFold}>{customContent}</SpaceMenuCustomContent>
                            {showCustomVolumeSection && renderVolumeSection()}
                        </>
                    ) : (
                        <>
                            <SpaceMenuTopSlot>
                                {isFold ? (
                                    <FoldToggleSection $isFold={isFold}>{renderFoldToggleButton()}</FoldToggleSection>
                                ) : (
                                    <SpaceMenuTitleSection>
                                        <SpaceMenuTitleLabel>스페이스</SpaceMenuTitleLabel>
                                        <SpaceMenuTitleRow>
                                            <SpaceMenuTitle>{currentSpaceName}</SpaceMenuTitle>
                                            <SpaceMenuHeaderActions>
                                                <SpaceMenuHeaderActionButton
                                                    type="button"
                                                    onClick={() => navigate(`/${space_uuid}/management`)}
                                                >
                                                    <Tooltip message={"관리"}>
                                                        <Icon
                                                            name={"setting"}
                                                            size={"16px"}
                                                            color={"var(--color-Grey1)"}
                                                        />
                                                    </Tooltip>
                                                </SpaceMenuHeaderActionButton>
                                                <SpaceMenuHeaderActionButton type="button" onClick={toggleSidebar}>
                                                    <Tooltip message={"닫기"}>
                                                        <Icon
                                                            name={"sidemenuClose"}
                                                            size={"16px"}
                                                            color={"var(--color-Grey1)"}
                                                        />
                                                    </Tooltip>
                                                </SpaceMenuHeaderActionButton>
                                            </SpaceMenuHeaderActions>
                                        </SpaceMenuTitleRow>
                                    </SpaceMenuTitleSection>
                                )}
                            </SpaceMenuTopSlot>
                            <SpaceMenuTitleDivider />
                            <SpaceMenuGroup>
                                <SpaceMenuNavList>
                                    {PRIMARY_SPACE_MENU_ITEMS.map((menuItem) => (
                                        <SpaceMenuSection key={menuItem.key} $isFold={isFold}>
                                            {renderSpaceMenuItem({
                                                to: `/${space_uuid}/${menuItem.path}`,
                                                isActive: primaryMenuActiveMap[menuItem.key],
                                                iconName: menuItem.iconName,
                                                label: menuItem.label,
                                            })}
                                        </SpaceMenuSection>
                                    ))}
                                </SpaceMenuNavList>
                            </SpaceMenuGroup>
                            <SpaceMenuGroup>
                                <ReferenceSection>
                                    <ReferenceMenuRow $isFold={isFold}>
                                        {renderSpaceMenuItem({
                                            to: `/${space_uuid}/reference`,
                                            isActive: isPathReference,
                                            iconName: "sideBarReference",
                                            label: "참고문헌",
                                            state: { folder: [] },
                                        })}
                                    </ReferenceMenuRow>
                                    <ReferenceDetailsPanel
                                        isFold={isFold}
                                        activeTab={activeTab}
                                        handleTabClick={handleTabClick}
                                        location={location}
                                        setToast={setToast}
                                        tagDelete={tagDelete}
                                        navigate={navigate}
                                        space_uuid={space_uuid}
                                        searchParams={searchParams}
                                        showTagAddModal={showTagAddModal}
                                        setShowTagAddModal={setShowTagAddModal}
                                        showFolderAddModal={showFolderAddModal}
                                        setShowFolderAddModal={setShowFolderAddModal}
                                        user={user}
                                    />
                                </ReferenceSection>
                            </SpaceMenuGroup>
                            {renderVolumeSection()}
                        </>
                    )}
                </SpaceMenuContentsWrap>
            </SpaceMenuContents>
            <SpaceMenuResizerWrap
                $device={device}
                $isFold={isFold}
                $isHovering={wBarHover}
                onMouseDown={startWBarResizing}
                onMouseEnter={() => setWBarHover(true)}
                onMouseLeave={() => setWBarHover(false)}
            >
                <SpaceMenuResizerLine $device={device} $isHovering={wBarHover} $isFold={isFold} />
            </SpaceMenuResizerWrap>
        </SpaceMenuContainer>
    );
}

export default SpaceMenuBar;
